<template>
  <div class="tw-flex tw-flex-col tw-h-full" data-test="tc-details-panel">
    <div v-if="loading || error" tabindex="0">
      <div class="tw-text-right tw-p-3">
        <WdsButtonIcon sm data-test="tc-details-close" @click="emit('close')">
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </div>
      <div v-if="loading" class="tw-mt-[30%] tw-flex tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>
      <WdsAlert v-else error class="tw-mx-4">
        {{ error }}
        {{ $t('An error occurred') }}
      </WdsAlert>
    </div>
    <div v-else class="tw-flex tw-flex-col tw-h-full" tabindex="0">
      <div class="gradient">
        <WdsAvatar
          :path="worker?.avatarPath ?? ''"
          :initials="worker?.initials"
          :alt="workerName"
          class="profile-avatar"
          lg
        />
        <div class="tw-float-right tw-p-3">
          <WdsButtonIcon sm data-test="tc-details-close" @click="emit('close')">
            <WdsIcon name="close" :size="16" />
          </WdsButtonIcon>
        </div>
      </div>

      <div class="detail-header" data-test="tc-details-header">
        <WdsStack direction-row justify-space-between gap-sm>
          <div class="tw-basis-2/3">
            <WdsStack direction-column justify-space-between gap-xs>
              <WorkerName
                :manages-workers="hireWithAttribution?.recruiterManagesWorkers ?? false"
                :recruiter-name="hireWithAttribution?.recruiter?.name"
                :recruiter-fees="hireWithAttribution?.fees ?? []"
                :recruiter-ownership-start-date="
                  hireWithAttribution?.recruiterOwnershipStartDate
                "
                :recruiter-ownership-days="hireWithAttribution?.recruiterOwnershipDays"
                :recruiter-ownership-days-left="
                  hireWithAttribution?.recruiterOwnershipDaysLeft
                "
              >
                <template #name>
                  <WdsText :text="workerName" :title="workerName" xl />
                </template>
              </WorkerName>
              <WdsText
                v-if="subtitleText"
                :text="subtitleText"
                :title="subtitleText"
                large
                secondary
              />
              <WdsText
                v-if="worker?.engagedViaSupplierName"
                small
                secondary
                data-test="tc-details-engaged-via"
              >
                {{ $t('Engaged via :name', { name: worker.engagedViaSupplierName }) }}
              </WdsText>
            </WdsStack>
          </div>

          <div class="tw-basis-1/3">
            <WdsStack direction-column gap-sm align-center>
              <TrustedContactStatusLabel
                v-if="trustedContact?.status"
                :status="trustedContact?.status"
                md
              />

              <WdsLabel v-if="worker?.isCurrentlyHired && !props.forOrganisation" subtle primary md>
                {{ $t('Currently hired') }}
              </WdsLabel>

              <template v-if="!isMspMediated">
                <WdsText v-if="!hasDayRate" :sub-text="$t('Rate negotiable')" />
                <WdsStack v-else direction-row align-center gap-xs>
                  <WdsText :text="`${dayRate}`" medium />
                  <WdsText :sub-text="`/ ${$t('day')}`" medium />
                </WdsStack>
              </template>
            </WdsStack>
          </div>
        </WdsStack>

        <WdsAlert v-if="approveValidationErrors.length" error class="tw-mt-2">
          <ul v-if="approveValidationErrors.length > 1" class="tw-mb-0">
            <li v-for="validationError in approveValidationErrors" :key="validationError">
              {{ validationError }}
            </li>
          </ul>
          <div v-else>{{ approveValidationErrors[0] }}</div>
        </WdsAlert>

        <WdsStack direction-row gap-md align-center class="tw-mt-6">
          <TrustedContactApprove
            v-if="!viewOnly && trustedContact?.viewerCanApprove"
            :trusted-contact-id="trustedContact?.id"
            @approved="updated"
            @error="handleApproveError"
          >
            <template #default="{ loading: loadingApproval }">
              <WdsButton :disabled="loadingApproval" tertiary data-test="tc-details-approve">
                {{ $t('Approve') }}
              </WdsButton>
            </template>
          </TrustedContactApprove>
          <a
            v-else-if="!viewOnly && worker?.viewerCanOfferJob"
            :href="
              laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
                freelancer: worker?.id,
              })
            "
          >
            <WdsButton primary data-test="tc-details-offer-job">{{ $t('Hire') }}</WdsButton>
          </a>
          <a v-else-if="!viewOnly && trustedContact?.viewerCanReInvite">
            <WdsButton primary data-test="tc-details-re-invite" @click="toggleReInviteModal">
              {{ $t('Invite to onboard') }}
            </WdsButton>
          </a>
          <a
            v-if="!viewOnly && worker?.viewerCanContact"
            :href="
              laravelNamedRoute(namedRoutes.frontend.user.profile.invite, {
                freelancer: worker?.id,
              })
            "
          >
            <WdsButton tertiary data-test="tc-details-message">{{ $t('Message') }}</WdsButton>
          </a>
          <div class="tw-self-stretch">
            <WdsMenu
              v-if="trustedContact?.viewerCanUpdate || trustedContact?.viewerCanDelete"
              z-index="21"
              placement="bottom-end"
              class="tw-self-stretch"
            >
              <template #trigger>
                <WdsButtonGhost class="tw-h-full tw-items-center" data-test="tc-details-menu">
                  <WdsIcon name="overflow-menu-horizontal" class="tw-cursor-pointer" />
                </WdsButtonGhost>
              </template>
              <WdsMenuOption
                v-if="!viewOnly && trustedContact?.viewerCanUpdate"
                data-test="tc-details-edit"
                @click="toggleEditModal"
              >
                <WdsIcon :size="16" name="edit" />
                {{ $t('Edit contact') }}
              </WdsMenuOption>

              <WdsMenuOption
                v-if="!viewOnly && trustedContact?.viewerCanDelete"
                theme="DESTRUCTIVE"
                data-test="tc-details-delete"
                @click="toggleDeleteModal"
              >
                <WdsIcon :size="16" name="trash-can" />
                {{ $t('Delete') }}
              </WdsMenuOption>
            </WdsMenu>
          </div>
        </WdsStack>
      </div>
      <WdsTabs
        v-model:active-tab-key="activeUrlTab"
        fitted
        secondary
        @update:active-tab-key="$emit('tab-changed')"
      >
        <WdsTab
          v-if="!!workerResult?.worker"
          :title="$t('Profile')"
          tab-key="trusted-contact-tab"
          class="tw-my-2 tw-mx-6"
          data-test="tc-profile-tab-button"
        >
          <!--    The null check fixes a typescript issue as it expects undefined but we can be both undefined and null      -->
          <WorkerProfileTab
            v-if="!loading"
            :trusted-contact="trustedContact"
            :worker="workerResult?.worker"
            data-test="tc-profile-tab"
            @open-edit-modal="toggleEditModal"
          />
        </WdsTab>
        <WdsTab
          v-if="shouldShowActivityTab"
          :title="$t('Activity')"
          tab-key="trusted-contact-activity"
          class="tw-my-2 tw-mx-6"
          data-test="tc-activity-tab-button"
        >
          <WorkerActivityTab
            v-if="!loading"
            :trusted-contact="trustedContact"
            :worker="workerResult?.worker"
            data-test="tc-activity-tab"
            :conversations="conversations"
          />
        </WdsTab>
        <WdsTab
          v-if="shouldShowComplianceTab"
          :title="$t('Compliance')"
          tab-key="trusted-contact-compliance"
          class="tw-my-2 tw-mx-6"
          data-test="tc-compliance-tab-button"
        >
          <WorkerComplianceTab
            v-if="!loading"
            :trusted-contact="trustedContact"
            :worker="workerResult?.worker"
            data-test="tc-compliance-tab"
          />
        </WdsTab>
        <WdsTab
          v-if="trustedContact?.id"
          :title="$t('Notes')"
          tab-key="trusted-contact-notes"
          class="tw-my-2 tw-mx-6"
          data-test="tc-notes-tab-button"
        >
          <template #title>
            <WdsStack align-center gap-sm data-test="tc-notes-tab-title">
              {{ $t('Notes') }}
              <WdsLabel v-if="notesCount > 0" xs>{{ notesCount }}</WdsLabel>
            </WdsStack>
          </template>

          <TrustedContactNotesTab
            v-if="!loading && trustedContact"
            :trusted-contact="trustedContact"
            :account-id="viewerAccountId"
            :for-organisation="forOrganisation"
            data-test="tc-notes-tab"
            @update-notes="refetch"
          />
        </WdsTab>
      </WdsTabs>
    </div>
  </div>

  <Teleport v-if="trustedContact?.id && !viewOnly" to="#modals">
    <TrustedContactManageModal
      :show="editModalIsOpen"
      :account-id="viewerAccountId"
      :trusted-contact-id="trustedContact.id"
      data-test="trusted-contact-manage-modal"
      @cancel="toggleEditModal"
      @close="toggleEditModal"
      @updated="updated"
    />

    <TrustedContactDeleteModal
      :show="deleteModalIsOpen"
      :trusted-contact-id="trustedContact.id"
      @cancel="toggleDeleteModal"
      @close="toggleDeleteModal"
      @deleted="deleted"
    />

    <TrustedContactReInviteModal
      :trusted-contact-id="trustedContact.id"
      :show="reInviteModalIsOpen"
      @close="toggleReInviteModal"
      @invited="updated"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, provide, readonly } from 'vue'
import {
  WdsAlert,
  WdsTabs,
  WdsTab,
  WdsMenu,
  WdsMenuOption,
  WdsIcon,
  WdsStack,
  WdsButton,
  WdsButtonIcon,
  WdsText,
  WdsButtonGhost,
  WdsSpinner,
  WdsAvatar,
  WdsLabel,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { toFormattedAmountByLocale } from '@/utils/currency'
import {
  WORKER_VIEW_FRAGMENT,
  TRUSTED_CONTACT_VIEW_FRAGMENT,
} from '@/components/WorkerDetailPanel/graphql/fragments'
import {
  ContactDetailConversationQuery,
  TrustedContactsViewQuery,
} from '@/components/Contacts/CompanyFreelancer/queries/TrustedContact'

import { WorkerViewQuery } from '@/components/WorkerDetailPanel/graphql/queries'
import TrustedContactManageModal from '@/components/Contacts/CompanyFreelancer/TrustedContactManageModal.vue'
import TrustedContactDeleteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactDeleteModal.vue'
import TrustedContactApprove from '@/components/Contacts/CompanyFreelancer/TrustedContactApprove.vue'
import WorkerActivityTab from '@/components/WorkerDetailPanel/PanelTabs/WorkerActivityTab.vue'
import WorkerProfileTab from '@/components/WorkerDetailPanel/PanelTabs/WorkerProfileTab.vue'
import WorkerComplianceTab from '@/components/WorkerDetailPanel/PanelTabs/WorkerComplianceTab.vue'
import TrustedContactStatusLabel from '@/components/Contacts/CompanyFreelancer/TrustedContactStatusLabel.vue'
import TrustedContactNotesTab from '@/components/WorkerDetailPanel/PanelTabs/TrustedContactNotesTab.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { getWorkerDisplayName, getWorkerDisplayTitle } from '@/utils/worker'
import { useFragment } from '@/gql'
import { ViewOnlyInterface, viewOnlyKey } from '@/components/WorkerDetailPanel/injectionKeys'
import WorkerName from '@/components/WorkerName.vue'
import TrustedContactReInviteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactReInviteModal.vue'
import { BusinessEntityType, MarketCode } from '@/gql/graphql'

const props = defineProps<{
  viewerAccountId: string
  workerId: string
  tabKey?: string
  forOrganisation?: boolean
  viewOnly?: boolean
}>()

const propViewOnly = ref(props.viewOnly)
provide<ViewOnlyInterface>(viewOnlyKey, {
  viewOnly: readonly(propViewOnly),
})

const emit = defineEmits(['close', 'updated', 'deleted', 'tab-changed'])

const activeUrlTab = ref(props.tabKey)

const variables = computed(() => ({
  companyId: currentAccount.globalId,
  workerId: props.workerId,
  workers: [props.workerId],
}))

const {
  loading: trustedContactLoading,
  result: trustedContactResult,
  error: trustedContactError,
  refetch,
} = useQuery(TrustedContactsViewQuery, variables, {
  clientId: 'publicApiCustomFields',
})

const {
  loading: workerLoading,
  result: workerResult,
  error: workerError,
} = useQuery(WorkerViewQuery, variables, {
  clientId: 'publicApiCustomFields',
})

const {
  loading: conversationLoading,
  result: conversationResults,
  error: conversationError,
} = useQuery(ContactDetailConversationQuery, variables, {
  clientId: 'publicApiCustomFields',
})

const error = computed(
  () => trustedContactError.value || workerError.value || conversationError.value,
)

const loading = computed(
  () => trustedContactLoading.value || workerLoading.value || conversationLoading.value,
)

const trustedContact = computed(() =>
  useFragment(
    TRUSTED_CONTACT_VIEW_FRAGMENT,
    props.forOrganisation
      ? trustedContactResult.value?.organisationTrustedContacts.data[0]
      : trustedContactResult.value?.trustedContacts.data[0],
  ),
)

const worker = computed(() => useFragment(WORKER_VIEW_FRAGMENT, workerResult.value?.worker))
const hireWithAttribution = computed(() => worker.value?.hiresWithAttribution[0])
const conversations = computed(() => conversationResults.value?.conversations.data ?? [])
const notesCount = computed(() => trustedContact.value?.notes.paginatorInfo.total ?? 0)

const editModalIsOpen = ref(false)
const deleteModalIsOpen = ref(false)
const reInviteModalIsOpen = ref(false)
const toggleEditModal = () => {
  editModalIsOpen.value = !editModalIsOpen.value
}

const toggleDeleteModal = () => {
  deleteModalIsOpen.value = !deleteModalIsOpen.value
}

const toggleReInviteModal = () => {
  reInviteModalIsOpen.value = !reInviteModalIsOpen.value
}

const workerName = computed(() => getWorkerDisplayName(worker.value?.name))

const hasDayRate = computed(() => (worker.value?.dayRate ?? 0) > 0)

const dayRate = computed(() => {
  const formattedDayRate = toFormattedAmountByLocale((worker.value?.dayRate ?? 0).toFixed(2))

  return `${worker.value?.currency} ${formattedDayRate}`
})

const jobTitle = computed(() =>
  getWorkerDisplayTitle(
    {
      name: worker.value?.name,
      jobTitle: worker.value?.jobTitle,
      email: worker.value?.email,
    },
    $t('Trusted Contact'),
  ),
)

// When the worker is engaged through an MSP, the client is not the
// party managing them — we strip the panel down to the basics (name,
// who manages them, contact details) and hide rate, activity and
// compliance, which the MSP owns.
const isMspMediated = computed(() => !!worker.value?.engagedViaSupplierName)

// For MSP-mediated workers, drop the "Trusted Contact" fallback — they
// aren't the client's TC, and the "Engaged via …" line below already
// covers their relationship to the company. Keep the real job title
// when set; otherwise render nothing.
const subtitleText = computed(() => {
  if (isMspMediated.value) {
    return getWorkerDisplayTitle({
      name: worker.value?.name,
      jobTitle: worker.value?.jobTitle,
      email: worker.value?.email,
    })
  }

  return jobTitle.value
})

const shouldShowActivityTab = computed(() => {
  if (isMspMediated.value) return false

  const hasActiveHires = (worker?.value?.activeHires?.paginatorInfo?.total ?? 0) > 0
  const hasCompletedHires = (worker?.value?.completedHires?.data?.length ?? 0) > 0
  const hasConversations = (conversations.value?.length ?? 0) > 0

  return hasActiveHires || hasCompletedHires || hasConversations
})

const shouldShowComplianceTab = computed(() => {
  if (isMspMediated.value) return false

  const workerMarket = worker.value?.market?.code
  const workerCountry = worker.value?.address?.country?.isoCode

  if (
    workerMarket === MarketCode.Us &&
    workerCountry === 'US' &&
    currentAccount.isUsWorkerClassificationApplicable
  ) {
    return true
  }

  if (workerMarket === MarketCode.Uk && workerCountry === 'GB') {
    const businessEntityType = worker.value?.primaryBusinessEntity?.type

    if (
      businessEntityType === BusinessEntityType.Umbrella ||
      businessEntityType === BusinessEntityType.SoleTrader
    ) {
      return false
    }

    return true
  }

  if (workerMarket === MarketCode.Nl || workerCountry === 'NL') {
    return true
  }

  return false
})

const updated = async () => {
  await refetch()
  emit('updated')
}

const deleted = () => {
  emit('deleted')
  emit('close')
}

const approveValidationErrors = ref([])

const handleApproveError = (errors) => {
  approveValidationErrors.value = errors.length ? errors : $t('An error occurred')
}
</script>

<style scoped lang="scss">
.profile-avatar {
  @apply tw-w-20;
  @apply tw-h-20;
  @apply tw-top-[50px];
  @apply tw-absolute;
  @apply tw-ml-7;
}

.gradient {
  @apply tw-shrink-0;
  @apply tw-h-[100px];
  @apply tw-bg-gradient-to-r tw-from-indigo-400 tw-to-red-300;

  :deep(.wds-button-icon-container) {
    .wds-button-icon {
      @apply tw-bg-black/10;
    }

    &:hover {
      .wds-button-icon {
        @apply tw-bg-black/[0.14];
      }
    }
  }
}

.detail-header {
  @apply tw-px-7;
  @apply tw-pb-7;
  @apply tw-mt-8;
  @apply tw-pt-1.5;

  :deep(.wds-popover-trigger) {
    @apply tw-h-full;
    .wds-menu-trigger {
      @apply tw-h-full;
    }
  }
}
</style>
